/**
 * Theme context — gestion light / dark / system.
 *
 * Stratégie de persistance :
 *   1. localStorage (clé 'vpc.theme') — disponible avant l'auth, persiste même déconnecté
 *   2. BDD (champ users.theme_preference) — Phase 2A.1 L4 (endpoint /api/users/me/theme)
 *
 * En L1, on persiste UNIQUEMENT localStorage. La sync BDD se fait en L4.
 *
 * Convention :
 *   - 'light' / 'dark' : choix explicite de l'utilisateur
 *   - 'system' : suit la préférence OS (prefers-color-scheme)
 *
 * Le mode résolu (light ou dark) est appliqué via data-theme="..." sur <html>
 * pour permettre les sélecteurs CSS et Tailwind darkMode='class' (qu'on
 * configure aussi pour matcher [data-theme="dark"]).
 *
 * --- Refonte R1 (mai 2026) ---
 * Default : 'dark' (DA moderne mockups, MODERNE 2026 validés).
 * Le mode 'light' reste disponible via le toggle, et bascule sur la palette
 * institutionnelle claire (papier crème). Les valeurs OKLCH sont définies dans
 * src/styles/globals.css (variables --c-*) et switchent automatiquement.
 */

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';
import type { ThemePreference } from '@roronoa-collectivites/shared-types';

const LS_KEY = 'vpc.theme';

interface ThemeContextValue {
  preference: ThemePreference;
  resolved: 'light' | 'dark';
  setPreference: (p: ThemePreference) => void;
  toggle: () => void;
}

const ThemeContext = createContext<ThemeContextValue | null>(null);

function readInitialPreference(): ThemePreference {
  if (typeof window === 'undefined') return 'dark';
  try {
    const raw = window.localStorage.getItem(LS_KEY);
    if (raw === 'light' || raw === 'dark' || raw === 'system') return raw;
  } catch {
    // localStorage indisponible (mode privé strict, iframe sandboxée)
  }
  // R1 : dark par défaut (DA moderne mockups). L'utilisateur peut basculer
  // sur 'light' via le toggle ; sa préférence est persistée localStorage.
  return 'dark';
}

function resolveTheme(pref: ThemePreference): 'light' | 'dark' {
  if (pref === 'light' || pref === 'dark') return pref;
  if (typeof window === 'undefined') return 'dark';
  return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
}

export function ThemeProvider({ children }: { children: ReactNode }) {
  const [preference, setPreferenceState] = useState<ThemePreference>(readInitialPreference);
  const [resolved, setResolved] = useState<'light' | 'dark'>(() => resolveTheme(readInitialPreference()));

  // Suivre les changements OS si préférence = 'system'
  useEffect(() => {
    if (preference !== 'system') return;
    if (typeof window === 'undefined') return;
    const mql = window.matchMedia('(prefers-color-scheme: dark)');
    const onChange = () => setResolved(mql.matches ? 'dark' : 'light');
    mql.addEventListener('change', onChange);
    return () => mql.removeEventListener('change', onChange);
  }, [preference]);

  // Appliquer sur <html> dès que `resolved` change
  useEffect(() => {
    if (typeof document === 'undefined') return;
    const root = document.documentElement;
    root.setAttribute('data-theme', resolved);
    // Aussi classe Tailwind pour darkMode: 'class'
    if (resolved === 'dark') root.classList.add('dark');
    else root.classList.remove('dark');
    // color-scheme natif (form controls auto-stylés)
    root.style.colorScheme = resolved;
  }, [resolved]);

  // Recalcul du résolu quand la préférence change
  useEffect(() => {
    setResolved(resolveTheme(preference));
  }, [preference]);

  const setPreference = useCallback((p: ThemePreference) => {
    setPreferenceState(p);
    try {
      window.localStorage.setItem(LS_KEY, p);
    } catch {
      // ignore
    }
  }, []);

  const toggle = useCallback(() => {
    setPreference(resolved === 'dark' ? 'light' : 'dark');
  }, [resolved, setPreference]);

  const value = useMemo<ThemeContextValue>(
    () => ({ preference, resolved, setPreference, toggle }),
    [preference, resolved, setPreference, toggle]
  );

  return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>;
}

export function useTheme(): ThemeContextValue {
  const ctx = useContext(ThemeContext);
  if (!ctx) throw new Error('useTheme must be used inside <ThemeProvider>');
  return ctx;
}
